import moment from 'moment-timezone';
import React, { FunctionComponent } from 'react';
import { translate } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import FastImage from 'react-native-fast-image';
import Theme from '../theme';
import { CalorieIcon, LoadIcon } from '../ui/components/Icons';
import { getBodyGroupImage } from '../utils/bodyGroupImage';
import { getSkillLevelText } from '../utils/WorkoutSettingsTextHelpers';

interface IProps {
  isMobile: boolean;
  navigation?: any;
  fullname: string;
  workoutDuration: string;
  workoutDate: string;
  level: string;
  effortPoints?: string;
  effortPercentage?: string;
  calories?: string;
  cardioOrResistance: string;
  averageHeartRate?: string;
  bodyGroups: string;
  gender: string;
  showButtons: boolean;
  screenShotMode?: boolean;

  t(key: string, options?: any): string;
}

const MyWorkoutResultsCardComponent: FunctionComponent<IProps> = (props: IProps) => {
  const backgroundImage = require('../assets/images/background-gradient.png');

  function replacePlusWithSpace(str: string) {
    return str.replace(/\+/g, ' ');
  }

  const threeBodyGroups = replacePlusWithSpace(decodeURIComponent(props.bodyGroups)).split(',');
  const bodyGroupImage = getBodyGroupImage(threeBodyGroups, props.cardioOrResistance, props.gender);
  const { t } = props;
  return (
    <View style={props.isMobile && { marginTop: 20 }}>
      {
        props.screenShotMode &&
        <View style={styles.background}>
          <FastImage
            style={{
              width: '100%',
              height: '100%',
            }}
            source={backgroundImage}
          />
        </View>
      }
      {
        props.isMobile &&
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <View style={[{ flex: 1, paddingHorizontal: 10 }, styles.verticalDivider]}>
            <FastImage
              resizeMode='contain'
              style={{
                height: 70,
              }}
              source={require('../assets/images/logoslogan.png')}
            />
          </View>
          <View style={{ flex: 1, marginLeft: 10, alignItems: 'center' }}>
            <Text style={styles.headingText}>{t('myWorkoutResults')}</Text>
          </View>
        </View>
      }
      {
        !props.isMobile &&
        <Text style={[styles.headingText, { textAlign: 'center', fontSize: 30 }]}>{t('myWorkoutResults')}</Text>
      }
      <Text
        style={styles.nameAndDateText}>{`${replacePlusWithSpace(decodeURI(props.fullname))}, ${
        moment(parseInt(props.workoutDate, 10)).format('D MMM')
      }`}
      </Text>
      {
        props.averageHeartRate &&
        <View style={styles.metricsContainer}>
          <View style={[styles.metric, styles.verticalDivider]}>
            <LoadIcon color={Theme.WHITE} width={24} height={24}/>
            <Text style={styles.metricValue}>{props.effortPoints}</Text>
            <Text style={styles.metricName}>{t('effortPoints')}</Text>
          </View>
          <View style={[styles.metric, styles.verticalDivider]}>
            <CalorieIcon color={Theme.WHITE} width={24} height={24}/>
            <Text style={styles.metricValue}>{props.calories}</Text>
            <Text style={styles.metricName}>{t('KiloCalories')}</Text>
          </View>
          <View style={styles.metric}>
            <Text style={styles.effortPercent}>%</Text>
            <Text style={styles.metricValue}>{props.effortPercentage}</Text>
            <Text style={styles.metricName}>{t('effortPercent')}</Text>
          </View>
        </View>
      }
      <View style={styles.horizontalDivider}/>
      <View style={{ flexDirection: 'row' }}>
        <View style={{ flex: 1, justifyContent: 'center' }}>
          <Text style={styles.bodyGroupHeading}>{t('bodyGroups')}</Text>
          <FastImage
            resizeMode='contain'
            style={{
              height: 100,
            }}
            source={bodyGroupImage}
          />
        </View>
        <View style={{ flex: 1 }}>
          <View style={styles.detailContainer}>
            <Text style={styles.detailTitle}>{t('duration')}</Text>
            <Text style={styles.detailBody}>{props.workoutDuration} Min</Text>
          </View>
          <View style={styles.horizontalDivider}/>
          <View style={styles.detailContainer}>
            <Text style={styles.detailTitle}>{t('type')}</Text>
            <Text style={styles.detailBody}>{props.cardioOrResistance}</Text>
          </View>
          <View style={styles.horizontalDivider}/>
          <View style={styles.detailContainer}>
            <Text style={styles.detailTitle}>{t('level')}</Text>
            <Text style={styles.detailBody}>{getSkillLevelText[props.level]}</Text>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  headingText: {
    color: Theme.WHITE,
    textTransform: 'uppercase',
    fontSize: 20,
    fontFamily: Theme.Custom700,
    lineHeight: 20,
  },
  background: {
    position: 'absolute',
    top: -20,
    left: -20,
    zIndex: -1,
    width: '110%',
    height: '110%',
  },
  verticalDivider: {
    borderRightColor: Theme.GREY_MEDIUM,
    borderRightWidth: 1,
  },
  horizontalDivider: {
    height: 1,
    width: '95%',
    backgroundColor: Theme.GREY_MEDIUM,
    alignSelf: 'center',
  },
  nameAndDateText: {
    color: Theme.WHITE,
    textTransform: 'uppercase',
    textAlign: 'center',
    fontSize: 19,
    fontFamily: Theme.Custom700,
    marginVertical: 20,
  },
  detailContainer: {
    paddingVertical: 10,
  },
  detailTitle: {
    color: Theme.WHITE,
    fontSize: 16,
  },
  detailBody: {
    color: Theme.WHITE,
    fontSize: 20,
    fontFamily: Theme.Custom700,
  },
  bodyGroupHeading: {
    color: Theme.WHITE,
    position: 'absolute',
    top: 5,
    left: 10,
    fontSize: 16,
    fontFamily: Theme.Custom700,
  },
  metricsContainer: {
    marginVertical: 10,
    flexDirection: 'row',
  },
  metric: {
    paddingVertical: 5,
    flex: 1,
    alignItems: 'center',
  },
  metricValue: {
    color: Theme.WHITE,
    fontSize: 28,
    lineHeight: 28,
    fontFamily: Theme.Custom700,
  },
  metricName: {
    color: Theme.WHITE,
  },
  effortPercent: {
    color: Theme.WHITE,
    fontSize: 24,
    lineHeight: 24,
  },
});

export const MyWorkoutResultsCard = translate(['auth', 'formFields'], { wait: true })(MyWorkoutResultsCardComponent);
